import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import tagpoLogo from '../static/images/TagpoLogo.png';
import { formatDate } from '../utils/formatting';

// Helper function to get localized text from bilingual object
function getLocalizedText(textObj, lang = 'en') {
  if (typeof textObj === 'string') return textObj; // Legacy support
  if (typeof textObj === 'object' && textObj !== null) {
    return textObj[lang] || textObj.en || '';
  }
  return '';
}

function LandingPage() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('tl') ? 'tl' : 'en';
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
  }, []);

  async function fetchEvents() {
    try {
      const response = await fetch('/api/events');
      if (response.ok) {
        const data = await response.json();
        filterEventsThisWeek(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }

  function filterEventsThisWeek(events) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate end of week (Sunday as last day or next 7 days?)
    // Using standard week definition (end on Saturday) consistent with typical calendars
    const endOfWeek = new Date(today);
    const dayOfWeek = endOfWeek.getDay(); // 0 (Sun) to 6 (Sat)
    const daysUntilEndOfWeek = 6 - dayOfWeek; // Days until Saturday
    endOfWeek.setDate(endOfWeek.getDate() + daysUntilEndOfWeek);
    // Set to end of the day
    endOfWeek.setHours(23, 59, 59, 999);

    const filtered = events.filter(event => {
      const eventDate = new Date(event.start_time);
      return eventDate >= today && eventDate <= endOfWeek;
    });

    // Sort by date soonest first
    filtered.sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    setUpcomingEvents(filtered);
  }

  return (
    <div className="landing-page">
      {/* Hero Section with Logo */}
      <section className="hero-section" style={{ textAlign: 'center', padding: '4rem 1rem 2rem' }}>
        <img
          src={tagpoLogo}
          alt="Tagpo Logo"
          className="hero-logo"
        />
        <h1 className="hero-title">{t('welcome_message')}</h1>
        <p className="hero-subtitle">{t('welcome_subtitle')}</p>

        {/* Welcome Statement Section */}
        <div className="welcome-section" style={{
          maxWidth: '800px',
          margin: '2rem auto',
          padding: '2rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '1rem',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#f6cc25' }}>{t('landing_welcome_community')}</h2>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: 'var(--text-primary)' }}>
            {t('landing_welcome_text')}
          </p>
        </div>

        <Link className="btn-primary-modern" to="/events" style={{ marginTop: '1rem' }}>
          {t('view_all_events')}
        </Link>
      </section>

      {/* Upcoming Events of the Week Section */}
      <section className="upcoming-events-section" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem 4rem' }}>
        <h2 className="section-title" style={{ textAlign: 'center', marginBottom: '2rem', borderBottom: '2px solid #f6cc25', display: 'inline-block', paddingBottom: '0.5rem', left: '50%', position: 'relative', transform: 'translateX(-50%)' }}>
          {t('this_weeks_events')}
        </h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div className="loading-spinner"></div>
            <p>{t('loading_events')}</p>
          </div>
        ) : upcomingEvents.length > 0 ? (
          <div className="grid-container" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {upcomingEvents.map(event => {
              const eventName = getLocalizedText(event.name, currentLang);
              const eventDesc = getLocalizedText(event.description, currentLang);
              return (
                <div key={event.id} className="card-modern" style={{ display: 'flex', flexDirection: 'column' }}>
                  <h5 className="card-title-modern">{eventName}</h5>
                  <p className="card-text-modern">
                    {eventDesc && eventDesc.length > 100
                      ? eventDesc.substring(0, 100) + '...'
                      : eventDesc}
                  </p>
                  <div className="card-meta" style={{ marginTop: 'auto' }}>
                    <div className="card-meta-item">
                      <span className="card-meta-label">{t('when')}</span>
                      <span>{formatDate(event.start_time, currentLang)}</span>
                    </div>
                    <div className="card-meta-item">
                      <span className="card-meta-label">{t('where')}</span>
                      <span>{event.place?.name || event.place || 'TBD'}</span>
                    </div>
                    {event.place?.address && (
                      <div className="card-meta-item">
                        <span className="card-meta-label">📍 {t('address')}</span>
                        <span>{event.place.address}</span>
                      </div>
                    )}
                  </div>
                  <Link
                    to="/events"
                    className="btn-outline-modern"
                    style={{ marginTop: '1rem', textAlign: 'center', display: 'block', textDecoration: 'none' }}
                  >
                    {t('details_signup')}
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state" style={{ textAlign: 'center', padding: '3rem', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '1rem' }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '1rem' }}>{t('no_events_this_week')}</p>
            <Link to="/events" className="btn-accent-modern btn-primary-modern">
              {t('check_upcoming_events')}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}

export default LandingPage;
